<template>
  <section class="gw-rules">
    <div class="gw-rules-head">
      <div>
        <h3>وضعیت و محدودیت درگاه</h3>
        <p>این تنظیمات با دکمهٔ «ثبت» پایین صفحه همراه اطلاعات درگاه ذخیره می‌شود و با غیرفعال کردن، اطلاعات درگاه پاک نمی‌شود.</p>
      </div>
      <span class="gw-rules-state" :class="savedRule.enabled ? 'is-on' : 'is-off'">
        {{ savedRule.enabled ? 'فعال' : 'غیرفعال' }}
      </span>
    </div>

    <p v-if="loading" class="gw-rules-muted">در حال بارگذاری...</p>

    <p v-else-if="!rulesRow" class="gw-rules-warn">
      ردیف «{{ rulesKey }}» در تنظیمات سایت پیدا نشد. ابتدا بک‌اند را به‌روزرسانی کنید و صفحه را دوباره باز کنید.
    </p>

    <template v-else>
      <div class="gw-rules-toggle">
        <b-form-checkbox v-model="enabled" switch size="lg">
          {{ enabled ? 'درگاه فعال است' : 'درگاه غیرفعال است' }}
        </b-form-checkbox>
        <small>
          در حالت غیرفعال، این درگاه به خریدار نمایش داده نمی‌شود و پرداخت با آن پذیرفته نمی‌شود؛
          اطلاعات اتصال درگاه باقی می‌ماند و هر زمان می‌توانید دوباره فعالش کنید.
        </small>
      </div>

      <div v-if="supportsCategories && !showCategories" class="gw-rules-add">
        <button type="button" class="gw-rules-link" @click="forceCategories = true">
          افزودن محدودیت دسته‌بندی (اختیاری)
        </button>
      </div>

      <div v-if="showCategories" class="gw-rules-cats">
        <label class="gw-rules-label">دسته‌بندی‌های مجاز</label>
        <small class="gw-rules-muted d-block mb-2">
          اگر هیچ دسته‌ای انتخاب نشود، این درگاه برای همهٔ کالاها نمایش داده می‌شود.
          انتخاب دستهٔ والد، زیردسته‌هایش را هم شامل می‌شود.
          این درگاه فقط وقتی در سبد خرید نمایش داده می‌شود که همهٔ کالاهای سبد از دسته‌های مجاز باشند.
        </small>

        <div class="gw-rules-chips">
          <span v-if="!selectedIds.length" class="gw-rules-muted">بدون محدودیت (همهٔ دسته‌ها)</span>
          <span v-for="id in selectedIds" :key="id" class="gw-rules-chip">
            {{ categoryName(id) }}
            <button type="button" title="حذف" @click="toggleCategory(id)">×</button>
          </span>
        </div>

        <div class="gw-rules-search">
          <input v-model="search" type="search" class="form-control" placeholder="جستجوی دسته‌بندی">
          <button type="button" class="gw-rules-link" @click="loadCategories">بارگذاری دوباره</button>
        </div>

        <p v-if="categoriesLoading" class="gw-rules-muted">در حال بارگذاری دسته‌بندی‌ها...</p>
        <p v-else-if="!rows.length" class="gw-rules-muted">دسته‌بندی‌ای پیدا نشد.</p>
        <div v-else class="gw-rules-tree">
          <label
              v-for="row in rows"
              :key="row.node.key"
              class="gw-rules-row"
              :class="{'is-inherited': inheritedFrom(row.node), 'is-match': row.isMatch}"
              :style="{'padding-right': `${row.node.depth * 18 + 6}px`}">
            <input
                type="checkbox"
                :checked="isSelected(row.node) || !!inheritedFrom(row.node)"
                :disabled="!!inheritedFrom(row.node)"
                @change="toggleCategory(row.node.data.id)">
            <span>{{ row.node.data.name }}</span>
            <small v-if="inheritedFrom(row.node)">(از طریق «{{ inheritedFrom(row.node) }}»)</small>
          </label>
        </div>
      </div>

      <p v-if="error" class="gw-rules-error">{{ error }}</p>
    </template>
  </section>
</template>

<script>
import {getAxiosInstance} from "@moeinmdn/mixitweb-front-modules/src/plugin/utils/setInstance.js";
import {
  normalizeCategories,
  mergeCategoryRecords,
  buildCategoryTree,
  filterCategoryTree,
  visibleCategoryRows,
  categoryKey,
} from "../category/categoryTree";
import {
  GATEWAY_RULES_KEY,
  findGatewayRulesRow,
  gatewayRuleOf,
  parseGatewayRules,
  saveGatewayRule,
} from "./gatewayRules";

export default {
  name: "GatewayRulesPanel",
  props: {
    gateway: {type: String, required: true},
    // installment gateways show the category list right away
    installment: {type: Boolean, default: false},
    supportsCategories: {type: Boolean, default: true},
  },
  data() {
    return {
      rulesKey: GATEWAY_RULES_KEY,
      loading: true,
      saving: false,
      error: "",
      enabled: true,
      selectedIds: [],
      forceCategories: false,
      search: "",
      records: Object.freeze([]),
      categoriesLoading: false,
    };
  },
  computed: {
    rulesRow() {
      return findGatewayRulesRow(this.$store.getters["SiteInfo/getSiteInfos"]);
    },
    savedRule() {
      return gatewayRuleOf(parseGatewayRules(this.rulesRow ? this.rulesRow.value : null), this.gateway);
    },
    showCategories() {
      return this.supportsCategories && (this.installment || this.forceCategories || this.savedRule.categories.length > 0);
    },
    dirty() {
      const a = [...this.selectedIds].sort().join(",");
      const b = [...this.savedRule.categories].sort().join(",");
      return this.enabled !== this.savedRule.enabled || a !== b;
    },
    tree() {
      return buildCategoryTree(this.records);
    },
    rows() {
      const filter = filterCategoryTree(this.tree, this.search);
      return visibleCategoryRows(this.tree, {filter});
    },
    selectedKeys() {
      return new Set(this.selectedIds.map(id => categoryKey(id)));
    },
  },
  watch: {
    gateway() {
      this.resetFromSaved();
    },
    rulesRow() {
      if (!this.saving) this.resetFromSaved();
    },
  },
  methods: {
    resetFromSaved() {
      this.enabled = this.savedRule.enabled;
      this.selectedIds = [...this.savedRule.categories];
      this.error = "";
    },
    async loadCategories() {
      this.categoriesLoading = true;
      try {
        const axios = getAxiosInstance();
        const [nested, flat] = await Promise.all([
          axios.get(`/dashboard/category`).catch(() => null),
          axios.get(`/dashboard/category/flat-categories?all=1`).catch(() => null),
        ]);
        const nestedData = nested && nested.data ? nested.data.data : null;
        const flatData = flat && flat.data ? flat.data.data : null;
        const nestedList = Array.isArray(nestedData) || (nestedData && typeof nestedData === "object" && !nestedData.meta)
            ? normalizeCategories(nestedData) : [];
        const flatList = Array.isArray(flatData) ? normalizeCategories(flatData) : [];
        this.records = Object.freeze(mergeCategoryRecords(nestedList, flatList));
      } catch (e) {
        console.log(e);
      } finally {
        this.categoriesLoading = false;
      }
    },
    isSelected(node) {
      return this.selectedKeys.has(node.key);
    },
    // name of the selected ancestor that already covers this node
    inheritedFrom(node) {
      let current = node.parent;
      while (current) {
        if (this.selectedKeys.has(current.key)) return current.data.name;
        current = current.parent;
      }
      return "";
    },
    categoryName(id) {
      const node = this.tree.byId.get(categoryKey(id));
      return node ? node.data.name : `دسته ${id}`;
    },
    toggleCategory(id) {
      const key = categoryKey(id);
      const index = this.selectedIds.findIndex(item => categoryKey(item) === key);
      if (index > -1) {
        this.selectedIds.splice(index, 1);
      } else {
        this.selectedIds.push(Number(id));
      }
      this.error = "";
    },
    /**
     * Called by the page's single «ثبت» button (Form.vue). No toast here; the page shows one.
     * Resolves true when something was saved, false when nothing changed; throws on failure.
     */
    async persist() {
      if (!this.dirty) return false;
      this.error = "";
      if (!this.rulesRow) {
        this.error = "تنظیمات وضعیت درگاه پیدا نشد. صفحهٔ تنظیمات را یک بار باز کنید.";
        throw new Error(this.error);
      }
      this.saving = true;
      try {
        await saveGatewayRule(this.rulesRow, this.gateway, {
          enabled: this.enabled,
          categories: this.selectedIds,
        });
        await this.$store.dispatch("SiteInfo/getSiteInfos");
        this.saving = false;
        this.resetFromSaved();
        return true;
      } catch (e) {
        const data = e && e.response && e.response.data;
        this.error = (data && (typeof data.message === "string" ? data.message : null)) || "ذخیرهٔ وضعیت درگاه انجام نشد.";
        throw e;
      } finally {
        this.saving = false;
      }
    },
  },
  async mounted() {
    if (!this.rulesRow) {
      try {
        await this.$store.dispatch("SiteInfo/getSiteInfos");
      } catch (e) {
        console.log(e);
      }
    }
    this.resetFromSaved();
    this.loading = false;
    if (this.supportsCategories) {
      this.loadCategories();
    }
  },
};
</script>

<style scoped>
.gw-rules {
  background: #fff;
  border: 1px solid rgba(235, 61, 96, 0.18);
  border-radius: 20px;
  padding: 18px 20px;
  margin-bottom: 18px;
  text-align: right;
}

.gw-rules-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.gw-rules-head h3 {
  font-size: 18px;
  font-weight: 800;
  margin: 0 0 6px;
}

.gw-rules-head p {
  margin: 0;
  color: #6b6b6b;
  line-height: 1.8;
}

.gw-rules-state {
  border-radius: 999px;
  padding: 4px 12px;
  font-weight: 800;
  white-space: nowrap;
}

.gw-rules-state.is-on {
  background: rgba(16, 185, 129, 0.14);
  color: #047857;
}

.gw-rules-state.is-off {
  background: rgba(180, 35, 24, 0.12);
  color: #b42318;
}

.gw-rules-toggle small,
.gw-rules-muted {
  color: #6b6b6b;
  line-height: 1.8;
}

.gw-rules-label {
  font-weight: 800;
  color: #1e1e1e;
  margin-top: 14px;
}

.gw-rules-link {
  border: 0;
  background: transparent;
  color: #eb3d60;
  font-weight: 700;
  padding: 0;
  white-space: nowrap;
}

.gw-rules-add {
  margin-top: 12px;
}

.gw-rules-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.gw-rules-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(235, 61, 96, 0.1);
  color: #b4234a;
  border-radius: 999px;
  padding: 3px 10px;
  font-weight: 700;
}

.gw-rules-chip button {
  border: 0;
  background: transparent;
  color: inherit;
  font-weight: 800;
  padding: 0;
  line-height: 1;
}

.gw-rules-search {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
}

.gw-rules-tree {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #ececed;
  border-radius: 14px;
  padding: 6px;
}

.gw-rules-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding-top: 4px;
  padding-bottom: 4px;
  border-radius: 8px;
  cursor: pointer;
}

.gw-rules-row:hover {
  background: rgba(235, 61, 96, 0.06);
}

.gw-rules-row.is-match span {
  font-weight: 800;
}

.gw-rules-row.is-inherited {
  color: #6b6b6b;
  cursor: default;
}

.gw-rules-row small {
  color: #8a8a8a;
}

.gw-rules-warn,
.gw-rules-error {
  color: #b42318;
  margin: 8px 0 0;
}
</style>
